import { defineStore } from 'pinia'
import { useAuthStore } from '@/features/account/session'
import apiService from '@/api'
import type { CharacterCreateData, CharacterUpdateData } from '@/api/contract'
import { normalizeAppImage, normalizeImageFocus } from '@/lib/media/imageAssetFields'
import type { Character } from '@/types/domain'

interface CharactersState {
  characters: Character[]
  currentCharacter: Character | null
  loading: boolean
  error: string | null
  lastFetched: number | null
  cacheDuration: number
  accountId: string | null
  generation: number
}

export const useCharactersStore = defineStore('characters', {
  state: (): CharactersState => ({
    characters: [],
    currentCharacter: null,
    loading: false,
    error: null,
    lastFetched: null,
    cacheDuration: 5 * 60 * 1000, // 5 minutes in milliseconds
    accountId: null,
    generation: 0,
  }),

  getters: {
    getCharacterById: (state) => (id: number | string): Character | undefined => {
      return state.characters.find(char => char.id === id)
    },

    getMyCharacters: (state) => (userId: string): Character[] => {
      return state.characters.filter(char => char.user_id === userId)
    },
  },

  actions: {
    setAccount(id: string | null) {
      if (this.accountId === id) return
      this.accountId = id
      this.generation++
      this.characters = []
      this.currentCharacter = null
      this.loading = false
      this.error = null
      this.lastFetched = null
    },

    assertLifetime(generation: number) {
      if (generation !== this.generation || this.accountId !== useAuthStore().userId) {
        throw new Error('The active account changed. Please try again.')
      }
    },

    async fetchCharacters(forceRefresh = false) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      // Check cache first unless forced refresh
      const now = Date.now()
      if (!forceRefresh && this.lastFetched && (now - this.lastFetched) < this.cacheDuration) {
        console.log('[CharactersStore] Using cached characters data')
        return this.characters
      }

      this.loading = true
      this.error = null
      try {
        const response = await apiService.getCharacters()
        this.assertLifetime(generation)
        this.characters = response.characters
        this.lastFetched = now // Update cache timestamp
        return this.characters
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to fetch characters'
        console.error('Failed to fetch characters:', error)
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async fetchMyCharacters() {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getMyCharacters()
        this.assertLifetime(generation)
        return response.characters
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to fetch your characters'
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async fetchCharacter(id: number | string) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        const response = await apiService.getCharacter(id)
        this.assertLifetime(generation)
        // Supabase adapter returns character directly, not wrapped in response.character
        const character = response
        this.currentCharacter = character

        // Update the character in the list if it exists, otherwise add it
        const index = this.characters.findIndex(char => char.id === id)
        if (index !== -1) {
          this.characters[index] = character
        } else {
          this.characters.push(character)
        }

        return character
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to fetch character'
        console.error('Failed to fetch character:', error)
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async createCharacter(characterData: CharacterCreateData) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        await apiService.createCharacter(characterData)
        this.assertLifetime(generation)
        // Refresh the characters list
        await this.fetchCharacters()
        this.assertLifetime(generation)
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to create character'
        console.error('Failed to create character:', error)
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async updateCharacter(id: number | string, characterData: CharacterUpdateData) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        await apiService.updateCharacter(id, characterData)
        this.assertLifetime(generation)
        const { img_asset: _asset, img_focus, ...fields } = characterData
        const safeCharacterData = {
          ...fields,
          ...(img_focus !== undefined ? { img_focus: normalizeImageFocus(img_focus) } : {}),
        }

        const applyUpdate = (character: Character): Character => {
          const updated = { ...character, ...safeCharacterData }
          if (fields.imgpath !== undefined) {
            updated.image = normalizeAppImage(null, { url: fields.imgpath, focus: updated.img_focus })
          } else if (img_focus !== undefined && updated.image) {
            updated.image = { ...updated.image, focus: updated.img_focus ?? null }
          }
          return updated
        }

        // Update the character in the list
        const index = this.characters.findIndex(char => char.id === id)
        if (index !== -1) {
          this.characters[index] = applyUpdate(this.characters[index])
        }

        // Update current character if it's the same one
        if (this.currentCharacter && this.currentCharacter.id === id) {
          this.currentCharacter = applyUpdate(this.currentCharacter)
        }
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to update character'
        console.error('Failed to update character:', error)
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    async deleteCharacter(id: number | string) {
      this.setAccount(useAuthStore().userId)
      const generation = this.generation
      this.loading = true
      this.error = null
      try {
        await apiService.deleteCharacter(id)
        this.assertLifetime(generation)

        // Remove from the list
        this.characters = this.characters.filter(char => char.id !== id)

        // Clear current character if it's the deleted one
        if (this.currentCharacter && this.currentCharacter.id === id) {
          this.currentCharacter = null
        }
      } catch (error: unknown) {
        this.assertLifetime(generation)
        this.error = error instanceof Error ? error.message : 'Failed to delete character'
        console.error('Failed to delete character:', error)
        throw error
      } finally {
        if (generation === this.generation && this.accountId === useAuthStore().userId) this.loading = false
      }
    },

    setCurrentCharacter(character: Character | null) {
      this.currentCharacter = character
    },

    clearError() {
      this.error = null
    },

    mergeCharacter(character: Character | null | undefined) {
      if (!character) return
      const normalized = character
      const index = this.characters.findIndex(char => char.id === normalized.id)
      if (index !== -1) {
        this.characters[index] = normalized
      } else {
        this.characters.push(normalized)
      }
      this.currentCharacter = normalized
    }
  }
})
